import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../config/firebase';
import authClient from '../client/authClient';
import { EmailVerificationConfirmation } from '../types/auth.types';
import { getResetPasswordErrorMessage, isDeadActionLink, isDeadVerificationLink } from '../utils/authUtils';
import { DEFAULT_POST_LOGIN_PATH } from '../utils/authRoutes';
import { allPasswordRulesMet, checkPasswordRules } from '../utils/passwordRules';
import { useCustomTokenSignIn } from './useCustomTokenSignIn';

/** The `mode` Firebase puts on a password-reset link. */
export const RESET_PASSWORD_MODE = 'resetPassword';
/** The `mode` the backend puts on a sign-up verification link. */
export const VERIFY_EMAIL_MODE = 'verifyEmail';

export type AuthActionMode = typeof RESET_PASSWORD_MODE | typeof VERIFY_EMAIL_MODE;

const toHandledMode = (mode: string | null): AuthActionMode | null =>
    mode === RESET_PASSWORD_MODE || mode === VERIFY_EMAIL_MODE ? mode : null;

/**
 * The link's one-time code: Firebase's `oobCode` for a password reset, the
 * backend's own `token` for email verification.
 */
const linkCode = (mode: AuthActionMode | null, params: URLSearchParams): string | null =>
    params.get(mode === VERIFY_EMAIL_MODE ? 'token' : 'oobCode');

/**
 * checking    → the code is being checked (resetPassword) or confirmed (verifyEmail)
 * unreachable → the check itself failed (network); says nothing about the link
 * form        → resetPassword only: the code is good, choose a new password
 * signing-in  → verifyEmail only: email confirmed, signing the person in
 * done        → password updated, or email confirmed without a sign-in
 * invalid     → expired, used, missing, or a mode this page does not handle
 */
export type AuthActionStatus = 'checking' | 'unreachable' | 'form' | 'signing-in' | 'done' | 'invalid';

type EmailConfirmation = { token: string; result: Promise<EmailVerificationConfirmation> } | null;

/**
 * Confirms a verification token with the backend, once per token: StrictMode
 * runs the effect twice in development, and a second confirm of a spent token
 * would report a link that just worked as dead. A failure forgets the token so
 * "Try again" really tries again.
 */
const confirmEmailOnce = (
    ref: MutableRefObject<EmailConfirmation>,
    token: string
): Promise<EmailVerificationConfirmation> => {
    if (ref.current?.token === token) return ref.current.result;
    const result = authClient.confirmEmailVerification(token);
    ref.current = { token, result };
    result.catch(() => {
        if (ref.current?.result === result) ref.current = null;
    });
    return result;
};

/**
 * `/auth/action?mode=…` — where emailed account links land.
 * `resetPassword` (`oobCode`) checks the code with Firebase and asks for a new
 * password. `verifyEmail` (`token`) never touches Firebase: the backend
 * confirms the address and, when it returns a custom token, the person is
 * signed in and lands where a fresh sign-in does. A sign-in that fails only
 * costs the automatic sign-in — the address is confirmed by then, so it falls
 * back to "Email confirmed" → sign in, never the expired-link screen. Any
 * other mode, or a missing code, gets the invalid-link screen.
 */
export const useAuthAction = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = toHandledMode(searchParams.get('mode'));
    const code = linkCode(mode, searchParams);
    const handled = !!mode && !!code;
    const emailConfirmation = useRef<EmailConfirmation>(null);
    const signInWithCustomToken = useCustomTokenSignIn();

    const [status, setStatus] = useState<AuthActionStatus>(handled ? 'checking' : 'invalid');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // Bumped by "Try again" to re-run the code check.
    const [checkAttempt, setCheckAttempt] = useState(0);

    useEffect(() => {
        if (!mode || !code) {
            setStatus('invalid');
            return undefined;
        }
        let cancelled = false;
        setStatus('checking');

        const signIn = async (customToken: string) => {
            setStatus('signing-in');
            try {
                await signInWithCustomToken(customToken);
            } catch {
                if (!cancelled) setStatus('done');
                return;
            }
            if (!cancelled) navigate(DEFAULT_POST_LOGIN_PATH, { replace: true });
        };

        if (mode === VERIFY_EMAIL_MODE) {
            confirmEmailOnce(emailConfirmation, code)
                .then((confirmation) => {
                    if (cancelled) return;
                    setEmail(confirmation.email);
                    if (confirmation.customToken) signIn(confirmation.customToken);
                    else setStatus('done');
                })
                .catch((err: any) => {
                    if (!cancelled) setStatus(isDeadVerificationLink(err) ? 'invalid' : 'unreachable');
                });
        } else {
            verifyPasswordResetCode(auth, code)
                .then((address) => {
                    if (cancelled) return;
                    setEmail(address);
                    setStatus('form');
                })
                .catch((err: any) => {
                    if (!cancelled) setStatus(isDeadActionLink(err?.code) ? 'invalid' : 'unreachable');
                });
        }
        return () => {
            cancelled = true;
        };
    }, [mode, code, checkAttempt, signInWithCustomToken, navigate]);

    const rules = checkPasswordRules(password, confirmPassword, email);
    const canSubmit = allPasswordRulesMet(rules) && !saving;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (mode !== RESET_PASSWORD_MODE || !code || !canSubmit) return;

        setError('');
        setSaving(true);
        try {
            await confirmPasswordReset(auth, code, password);
            setPassword('');
            setConfirmPassword('');
            setStatus('done');
        } catch (err: any) {
            if (isDeadActionLink(err?.code)) setStatus('invalid');
            else setError(getResetPasswordErrorMessage(err?.code));
        } finally {
            setSaving(false);
        }
    };

    // `replace`: the one-time link should not stay in the back stack.
    const goToSignIn = () =>
        navigate(email ? `/login?email=${encodeURIComponent(email)}` : '/login', { replace: true });
    const backToSignIn = () => navigate('/login', { replace: true });
    const retryCheck = () => setCheckAttempt((n) => n + 1);

    return {
        mode,
        status,
        email,
        password, setPassword,
        confirmPassword, setConfirmPassword,
        rules,
        canSubmit,
        saving,
        error,
        handleSubmit,
        goToSignIn,
        backToSignIn,
        retryCheck,
    };
};
